import React, { useState } from 'react'
import pluralize from 'pluralize'
import { Box, DialogActions } from '@mui/material'
import { GridColDef, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid'
import { useNavigate, useParams } from 'react-router-dom'

import { SubmissionsPageContextType } from './SubmissionsViewModel'
import { generatePDFZipFile } from '../../../domain/domain'
import { EdifyDataGrid } from '../../components/datagrid/EdifyDataGrid'
import { ErrorToast } from '../../../core/utils/toast-notifications/ToastNotifications'
import { ROUTE_SUBMISSIONS } from './SubmissionsPage'
import { extractAndMergeFilesToPDF } from '../../../core/utils/pdf-converter/PdfConverter'
import {
  getDynamicHeader,
  mock_columns,
  normalizeDynamicHeadersRows,
  normalizeSubmissionsMockDataGrid,
} from '../../components/datagrid/gridData'
import BulkDownloadButton from '../../components/buttons/BulkButtons/BulkDownloadButton'

import { EdifyButton } from '../../components/buttons'
import BasicSubmissionModal from '../submission/BasicSubmissionModal'

import { AppColors } from '../../Theme'
import { EyeIconGreen } from '../../../core/components/icons/EyeIcon'
import { PencilIconBlue } from '../../../core/components/icons/PenciIIcon'
import { CopyToClipBoard } from '../../../core/components/icons/CopyToClipBoard'
import { NotifyIcon } from '../../../core/components/icons/Notify'
import { GridItem } from '../../components/datagrid/gridItems'

interface ISubmissionPageFilterProps {
  vm: SubmissionsPageContextType
  title: string
  showPDF?: boolean
  modalButtonGroup?: boolean
  basicModal?: boolean
  handleCloseModal?: () => void
  handleAddSelected?: (selections: GridRowSelectionModel) => void
  selectedSubmissionIds?: string[]
  readOnlyFormJson?: any
}

interface ISubmissionActionProps {
  id: string
  formId: string
  navigate: (id: string) => void
  canEdit: boolean
  canView: boolean
  canCreate: boolean
  loading: boolean
  onNotify: (id: string) => void
  onCopy: (id: string) => void
}

const SubmissionActions: React.FC<ISubmissionActionProps> = ({
  navigate,
  formId,
  id,
  canView,
  canEdit,
  canCreate,
  loading,
  onCopy,
  onNotify,
}) => {
  const nav = (e: any, url: string) => {
    e.stopPropagation()
    navigate(url)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flex: 1,
        marginRight: '12px',
      }}
      onClick={e => e.stopPropagation()}
    >
      {canView ? (
        <EdifyButton
          disabled={loading}
          loading={false}
          secondary
          noBackground
          textStyle={{
            color: AppColors.success600,
          }}
          buttonStyle={{ backgroundColor: AppColors.baseWhite }}
          title='View'
          onClick={(e: any) => nav(e, `/forms${ROUTE_SUBMISSIONS}/${formId}/submission/${id}`)}
          icon={<EyeIconGreen />}
        />
      ) : (
        ''
      )}
      {canEdit ? (
        <EdifyButton
          disabled={loading}
          loading={false}
          secondary
          noBackground
          textStyle={{
            color: AppColors.primary600,
          }}
          buttonStyle={{ backgroundColor: AppColors.baseWhite }}
          title='Edit'
          onClick={(e: any) => nav(e, `/forms${ROUTE_SUBMISSIONS}/${formId}/submission/${id}/edit`)}
          icon={<PencilIconBlue />}
        />
      ) : (
        ''
      )}
      {canEdit ? (
        <EdifyButton
          disabled={false}
          loading={loading}
          secondary
          noBackground
          textStyle={{
            color: AppColors.d600,
          }}
          buttonStyle={{ backgroundColor: AppColors.baseWhite }}
          title='Notify'
          onClick={(e: any) => {
            e.stopPropagation()
            onNotify(id)
          }}
          icon={<NotifyIcon />}
        />
      ) : (
        ''
      )}

      {canView && canCreate ? (
        <EdifyButton
          disabled={loading}
          loading={false}
          secondary
          noBackground
          textStyle={{
            color: AppColors.g500,
          }}
          buttonStyle={{ backgroundColor: AppColors.baseWhite }}
          title='Duplicate'
          onClick={(e: any) => {
            e.stopPropagation()
            onCopy(id)
            //TODO. INSERT COPY OF SUBMISSION DATA
            nav(e, `/forms${ROUTE_SUBMISSIONS}/${formId}/submission/${id}/editCopy`)
          }}
          icon={<CopyToClipBoard />}
        />
      ) : (
        ''
      )}
    </Box>
  )
}

const SubmissionDataGrid: React.FC<ISubmissionPageFilterProps> = ({
  vm,
  title,
  showPDF = true,
  modalButtonGroup,
  handleAddSelected,
  handleCloseModal,
  selectedSubmissionIds,
  basicModal = false,
  readOnlyFormJson,
}) => {
  const { formCategory } = useParams()
  const navigate = useNavigate()
  const { query, set } = vm.queryHelper

  const [downloadingPDF, setDownloadingPDF] = useState<boolean>(false)
  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>(() => selectedSubmissionIds ?? [])

  /**
   * Gets the column headers for the data grid.
   *
   * we have three cases isCorrectiveActionView(static), formHeaders(dynamic), and mock_columns(static)(to be removed)
   * @returns {GridColDef[]} An array of GridColDef representing the column headers.
   */
  const getColumns = (): GridColDef[] => {
    let columns
    if (!vm.form.webHeaders || vm.form.webHeaders.length === 0) {
      columns = mock_columns()
    } else {
      columns = getDynamicHeader(vm.normalizedDynamicHeaders ?? [], vm.hasCreatedAtHeader)
    }

    const canCreate = vm.form?.currentUserPermission?.create ?? 0
    const canEdit = vm.form?.currentUserPermission?.create ?? 0
    const canView = vm.form?.currentUserPermission?.read ?? 0

    let width = 150
    if (canEdit) {
      width = width + 200
    }
    if (canCreate) {
      width = width + 100
    }

    if (vm.form.isActionButtonsEnabled)
      columns.push({
        field: 'Actions',
        renderHeader: () => <GridItem sx={{ textAlign: 'center', fontWeight: 'bold' }} text='Actions' />,

        width: width, //400 if last action is only a button
        renderCell: ({ row }) => (
          <SubmissionActions
            formId={vm.form.id}
            id={row.id}
            navigate={navigate}
            loading={vm.notifyIds.includes(row.id)}
            onCopy={(id: string) => {
              console.log('Copy')
            }}
            onNotify={(id: string) => {
              console.log('Notify')
              vm.onNotify(id)
            }}
            canEdit={canEdit != 0}
            canView={canView != 0}
            canCreate={canCreate != 0}
          />
        ),
      })

    return columns
  }

  /**
   * Gets the column rows for the data grid.
   *
   * We have three cases isCorrectiveActionView(static), formHeaders(dynamic), and mock_columns(static)(to be removed)
   * check MUI DataGrid render rows for more info
   * @returns {any[]} An array of objects representing the column rows.
   */
  const getRows = (): any[] => {
    if (vm.submissionError || !vm.submissions) return []
    if (!vm.form.webHeaders || vm.form.webHeaders.length === 0) return normalizeSubmissionsMockDataGrid(vm.submissions)
    return normalizeDynamicHeadersRows(vm.submissions, vm.form, vm.normalizedDynamicHeaders, vm.connectedData)
  }

  /**
   * Handles the row click event in the data grid.
   *
   * note that we have two cases here isCorrectiveActionView and not isCorrectiveActionView so the id comes in different ways
   * @param {GridRowParams} params - The parameters object containing row data.
   */
  const handleRowClicked = (params: GridRowParams) => {
    const subId = params.row.id

    const formId = vm.form!.id
    if (basicModal) {
      setSubId(subId)
      setSubmissionOpen(true)
      return
    }

    navigate(`/${formCategory}${ROUTE_SUBMISSIONS}/${formId}/submission/${subId}`, {
      state: {},
    })
  }

  /**
   * Gets the PDF IDs for downloading.
   * @returns {string} A string representing the PDF IDs. ex 1;2;3
   *
   */
  const getPDFIds = (): string => {
    const selectedSubmissions = vm.allSubmissions?.filter(submission => selectionModel.includes(submission.id))

    const results = selectedSubmissions?.map(submission => {
      let afIds = ''
      let lfIds = ''

      if (submission.additionalFormSubmissions) {
        afIds = submission.additionalFormSubmissions.map(af => af.id).join(';')
      }

      if (submission.linkFormSubmissions) {
        lfIds = submission.linkFormSubmissions.reduce((acc, lf) => [...acc, ...lf.submissionIds], []).join(';')
      }

      return [submission.id, afIds, lfIds].filter(Boolean).join(';')
    })

    return results?.join(';') ?? ''
  }

  /**
   * Downloads the PDF files. can handle 1 - n files
   */
  const downloadPDF = async () => {
    if (!selectionModel.length) return
    setDownloadingPDF(true)
    const ids = getPDFIds()
    const res = await generatePDFZipFile(ids, 'list')
    if (res.isLeft() || !res.right) {
      ErrorToast({ title: 'Error downloading PDF.' })
      setDownloadingPDF(false)
      return
    }
    extractAndMergeFilesToPDF(res.right, vm.form?.name)
    setDownloadingPDF(false)
  }
  const addSelected = () => {
    handleAddSelected && handleAddSelected(selectionModel)
    handleCloseModal && handleCloseModal()
  }
  // if dynamic header have the the first key be what i s sorted.

  const firstFormHeader = query.sortBy ?? 'createdAt'
  const [submissionOpen, setSubmissionOpen] = useState(false)
  const [subId, setSubId] = useState('')

  return (
    <>
      <BasicSubmissionModal
        key={subId}
        open={submissionOpen}
        handleClose={() => setSubmissionOpen(false)}
        orgId={vm.form.orgId ?? ''}
        formId={vm.form.id}
        subId={subId}
        form={vm.form}
        formReadOnly={readOnlyFormJson}
      />
      <EdifyDataGrid
        title={title}
        noRowsText={`No ${pluralize(title)} found.`}
        renderHeaderButtons={
          showPDF ? (
            <BulkDownloadButton
              onClick={downloadPDF}
              downloadingPDF={downloadingPDF}
              noSelections={selectionModel.length == 0}
            />
          ) : (
            <></>
          )
        }
        columns={getColumns()}
        rows={getRows()}
        paginationMode='server'
        error={vm.submissionError}
        initSort={[
          {
            field: 'createdAt',
            sort: query.sortOrder,
          },
        ]}
        rowSelectionModel={selectionModel}
        setSelectionModel={setSelectionModel}
        handleRowClicked={basicModal ? handleRowClicked : modalButtonGroup ? undefined : handleRowClicked}
        loading={vm.isLoading || vm.isLoadMore}
        rowCount={vm.totalCount || 0}
        loadMoreRows={vm.loadMore}
        reload={vm.reloadSubmissions}
        sortClickedHandler={vm.sortClickedHandler}
      />
      {modalButtonGroup && (
        <DialogActions
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            p: 0,
            marginTop: '24px',
          }}
        >
          <EdifyButton data-testid='CloseDialogButton' noBackground title={'Cancel'} onClick={handleCloseModal} />
          <EdifyButton onClick={addSelected} title={'Add Selected Submissions'} />
        </DialogActions>
      )}
    </>
  )
}

export default SubmissionDataGrid
